import { IntegrationCard } from './IntegrationCard';
import { IntegrationTile } from './integrationLogos';
import { useState } from 'react';
import { useIntegration, useConnectLinear, useDisconnectLinear } from '../../hooks/useIntegrations';
import { useIntegrationAvailability } from '../../hooks/useIntegrationAvailability';
import { log } from '@/lib/logger';
import { LINEAR_SYNC_RECONNECT_COPY } from '@overlayqa/shared';

log.info('[LinearIntegrationCard] Module loading...');

export function LinearIntegrationCard() {
  const { integration, isConnected, isLoading, refetch } = useIntegration('linear');
  // Connected before Linear two-way sync existed: exports work, changes in
  // Linear never come back until a fresh sign-in (needsSyncReconnect).
  const needsSyncReconnect = isConnected === true && integration?.syncReconnectRequired === true;
  const { available, reason: unavailableReason } = useIntegrationAvailability('linear');
  const connectMutation = useConnectLinear();
  const disconnectMutation = useDisconnectLinear();

  const [actionError, setActionError] = useState<string | null>(null);

  const isConnecting = connectMutation.isPending;
  const isDisconnecting = disconnectMutation.isPending;

  const handleConnect = () => {
    // Never fire a connect the backend can't complete — it's a guaranteed 500.
    if (!available) return;
    setActionError(null);
    connectMutation.mutate(undefined, {
      onError: (err) => {
        setActionError(err.message || 'Failed to connect Linear. Try again.');
      },
    });
  };

  const handleDisconnect = () => {
    setActionError(null);
    disconnectMutation.mutate(undefined, {
      onError: (err) => {
        setActionError(err.message || 'Failed to disconnect Linear. Try again.');
      },
    });
  };

  return (
    <IntegrationCard
      provider="linear"
      loading={isLoading}
      tile={<IntegrationTile color="var(--color-linear)"><LinearIcon /></IntegrationTile>}
      name="Linear"
      description="Push captured issues to Linear with screenshots and full technical context."
      connection={{
        connected: isConnected,
        connecting: isConnecting,
        disconnecting: isDisconnecting,
        onConnect: handleConnect,
        onDisconnect: handleDisconnect,
        onRetry: () => void refetch(),
        available,
        unavailableReason,
        reconnectMessage: needsSyncReconnect ? LINEAR_SYNC_RECONNECT_COPY : undefined,
        connectedAt: integration?.createdAt,
        rows: [['Workspace', integration?.providerMetadata?.workspaceName]],
        twoWaySync: true,
      }}
      errors={[actionError]}
    />
  );
}

function LinearIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M1.22541 61.5228c-.2225-.9485.90748-1.5459 1.59638-.857L39.3342 97.1782c.6889.6889.0915 1.8189-.857 1.5964C20.0515 94.4522 5.54779 79.9485 1.22541 61.5228Z" fill="white"/>
      <path d="M.00189135 46.8891c-.01764375.2833.08887215.5599.28957165.7606L52.3503 99.7085c.2007.2007.4773.3075.7606.2896 2.3692-.1476 4.6938-.46 6.9624-.9259.7645-.157 1.0301-1.0963.4782-1.6481L2.57595 39.4485c-.55186-.5519-1.49117-.2863-1.648174.4782-.465915 2.2686-.77832 4.5932-.92588465 6.9624Z" fill="white"/>
      <path d="M4.21093 29.7054c-.16649.3738-.08169.8106.20765 1.1l64.77602 64.776c.2894.2894.7262.3742 1.1.2077 1.7861-.7956 3.5171-1.6927 5.1855-2.684.5521-.328.6373-1.0867.1832-1.5407L8.43566 24.3367c-.45409-.4541-1.21271-.3689-1.54074.1832-.99132 1.6684-1.88843 3.3994-2.68399 5.1855Z" fill="white"/>
      <path d="M12.6587 18.074c-.3701-.3701-.393-.9637-.0443-1.3541C21.7795 6.45931 35.1114 0 49.9519 0 77.5927 0 100 22.4073 100 50.0481c0 14.8405-6.4593 28.1724-16.7199 37.3375-.3903.3487-.984.3258-1.3542-.0443L12.6587 18.074Z" fill="white"/>
    </svg>
  );
}

log.info('[LinearIntegrationCard] Module loaded');
